/**
 * Infinite nested board — core types.
 *
 * A Space is a container with its own local coordinate system. Each space
 * reports its own `width`/`height` — the frame fits its contents instead of
 * forcing items to cram into a fixed canvas. Nested spaces are rendered
 * inside their parent Item's frame, so deep zoom compounds naturally through
 * DOM transforms.
 */
// Type-only, so this does not close a runtime cycle with `lifecycle-style.ts`
// (which imports the board types back).
import type { LifecyclePaint } from "./lifecycle-style.js";

// Default / fallback size for an empty space and the natural "page" unit used
// by viewport math before anything measures. Spaces can and do grow past it.
export const SPACE_SIZE = 1000;

export interface BoardItem {
  /** Stable id for this item, unique across the whole board. */
  readonly id: string;
  /**
   * The one logical entity this item is a placement OF. Absent on ordinary
   * items (they ARE their own entity — treat `logicalId ?? id`). Set on the
   * per-member duplicates of a `replicated`-mode aspect place: every copy
   * carries the place's canonical key, so selection / hover / focus / colour /
   * diff fan out to all copies and legend / search / minimap dedupe to one.
   */
  readonly logicalId?: string;
  readonly label: string;
  /** Position in parent space's coordinate system (0..SPACE_SIZE). */
  readonly x: number;
  readonly y: number;
  readonly w: number;
  readonly h: number;
  /** Inner space; when present the item can be zoomed into. */
  readonly space?: BoardSpace;
  /**
   * True when this item HAS a nested space, even if it isn't solved yet.
   * The lazy `BoardEngine` sets this on items below the requested depth so
   * the renderer can show a "divable" affordance and issue a deeper
   * `getSpace` request on demand. The eager `buildBoardFromModules` always
   * fills `space` directly, so for it `hasSpace === (space !== undefined)`.
   */
  readonly hasSpace?: boolean;
  /**
   * Cross-level dependency "ports". Rendered on the enclosing space's wall
   * when this item is at the focal level — so the user can see external
   * connections without losing the focal context.
   */
  readonly externalPorts?: readonly ExternalPort[];
  /**
   * Calls whose aggregated endpoints collapse into this same item — i.e. the
   * module calls another one of its own interfaces, or two of its deeply
   * nested descendants talk to each other but neither surfaces at this item's
   * level. Rendered inside the item so the process flow is still visible.
   */
  readonly internalCalls?: readonly CallDetail[];
  /**
   * Lifecycle paint for this item at the scrub point the space was folded at,
   * stamped by {@link applyLifecycle}.
   *
   * It rides on the ITEM rather than being looked up at the render site so that
   * a folded space paints itself: `renderBoardSvg` needs no lifecycle argument,
   * and a backend cannot render a folded space and silently omit the paint. The
   * HTTP board export did exactly that — it built its own space off the lazy
   * engine and rendered it plain, while the CLI and MCP renders of the same
   * workspace ghosted, struck and badged correctly.
   *
   * It is also what puts lifecycle in the JSON `BoardSpace` payload, so a client
   * reading the space over the wire can paint without re-deriving the timeline.
   */
  readonly lifecycle?: LifecyclePaint;
  /**
   * Figure/ground role for a `focus`ed view, stamped by {@link applyFocus}.
   * Absent on every item of an unfocused board — which is the whole board, for
   * every view that wrote no `focus` clause.
   *
   * Like {@link lifecycle} it rides on the ITEM so a folded space paints
   * itself; unlike it, the paint is NOT inside the composed node. Ground is
   * drawn as a veil at the ITEM layer — above the node, above the nested space,
   * and outside anything that fades the node — so a lifecycle-ghosted ground
   * node recedes by the same amount as a live one instead of less.
   */
  readonly focus?: FocusRole;
  /** Opaque pass-through for consumer data (e.g. the source Module). */
  readonly meta?: unknown;
}

/**
 * What a `focus` clause makes of one board object.
 *
 * `figure` — what the view is about. Painted exactly as it would be unfocused:
 * emphasis is spent on the GROUND, so focus scales the same at 3-of-300 and
 * 200-of-300, and never collides with the ring/glow channels the transient
 * gestures own.
 *
 * `ground` — present, readable, receded. Not hidden: an author who wanted it
 * gone had `hide`.
 */
export type FocusRole = "figure" | "ground";

/**
 * A board item's model key — the identity lifecycle paint (and anything else
 * keyed per logical entity) is looked up by.
 *
 * `logicalId` first: the per-member duplicates of a replicated aspect place all
 * carry the place's canonical key, so every copy resolves to the same paint.
 *
 * It lives HERE, beside the type, because `applyLifecycle` keys itself with it —
 * a copy of the rule anywhere else is a second way to key the same board, which
 * is how `#id`-carrying modules once exported as live while the live board
 * painted them correctly.
 *
 * KNOWN DIVERGENCE, predating the fold and deliberately not changed here: the
 * live board looks paint up by `moduleKey(module)` (`WidgetNode.tsx`), which is
 * the item's `id`, not its `logicalId`. The two agree for every ordinary module,
 * since `id` IS `moduleKey`. They part only on a synthetic aspect place-node,
 * whose `logicalId` is the aspect TARGET's id — so a replicated place standing
 * for a `@deprecated` module keys onto that module's paint here and onto
 * nothing on the live board. Which of the two is right is a design question
 * about whether a place inherits the lifecycle of what it stands for, not a
 * mechanical bug, so it wants an answer before either side moves.
 */
export function boardItemKey(item: BoardItem): string {
  const m = item.meta as { name?: string } | undefined;
  return (item.logicalId ?? item.id ?? m?.name ?? "").replace(/^::/, "");
}

/** The one logical entity a board item belongs to — its {@link BoardItem.logicalId}
 *  when it's a `replicated` copy, else its own id. The single key every
 *  doppelgänger-aware seam (selection, hover, highlight, minimap) compares on. */
export function logicalKeyOf(item: Pick<BoardItem, "id" | "logicalId">): string {
  return item.logicalId ?? item.id;
}

/**
 * Which way the calls behind a port run.
 *
 * `inout` is not a third kind of port — it is what a port IS once the same peer
 * has been seen from both ends. Identity is (owner, peer): one relationship,
 * one pill. Keying identity on direction instead produced two pills on opposite
 * walls for a single conversation, and the reader had to notice the labels
 * matched to work out they were the same peer.
 */
export type PortDirection = "out" | "in" | "inout";

export interface ExternalPort {
  readonly id: string;
  readonly direction: PortDirection;
  /** Displayed on the port, e.g. the peer's top-level module name. */
  readonly peerLabel: string;
  /** Stable id of the peer ancestor (at the edge's LCA level). */
  readonly peerId: string;
  /** Concrete endpoint paths that aggregated into this port, for drill-down. */
  readonly details: readonly CallDetail[];
  /** Interface-derived style for the segment this port draws — same aggregation
   *  as a `BoardEdge` (homogeneous keeps the style, heterogeneous → neutral), so
   *  a cross-space segment is styled exactly like a same-space edge. */
  readonly widgetStyle?: EdgeWidgetStyle;
  // No `focus` here. A port nub is drawn outside its owning item's box, so it
  // reads like it needs its own role — but the export renders no ports at all
  // and the live `PortsLayer` resolves the owning item's role directly. Add one
  // when a backend actually reads it.
  readonly widgetTag?: string;
  /** True for a view BOUNDARY stub (VIEWS §7): the peer is absent from the
   *  view — the nub marks a call crossing the view's edge, not a cross-space
   *  segment to a rendered node. */
  readonly boundary?: boolean;
  /** True for a live-conformance UNDOCUMENTED stub: this port was auto-derived
   *  from an observed-but-unmodeled cross-space call, not the model. A live
   *  overlay — painted as a violation (amber/dashed) and EXCLUDED from static
   *  export (see render-svg). */
  readonly violation?: boolean;
}

/** One concrete call recorded by a process, represented in UI form. */
export interface CallDetail {
  readonly fromId: string;
  readonly toId: string;
  readonly fromPath: string;
  readonly toPath: string;
  readonly fromInterfaceId?: string;
  readonly toInterfaceId?: string;
  readonly processId?: string;
  /** Zero-based index of the call within its process's step list. */
  readonly stepIndex?: number;
}

// Backwards-compat alias.
export type ExternalPortDetail = CallDetail;

/**
 * A cross-space CONNECTION as a first-class object: one logical link (deepest
 * source module → deepest target module), aggregating every call between them.
 * Its visible pieces — the LCA edge and the wall ports on the descent chain —
 * all reference the same connection by id, so a connection can be highlighted,
 * diffed, and queried AS A UNIT across zoom levels. Topology only: no geometry
 * (that's resolved at render). Built once, placement-independent.
 */
export interface Connection {
  /** Stable id, `${sourceId}=>${targetId}`. */
  readonly id: string;
  readonly sourceId: string;
  readonly targetId: string;
  readonly details: readonly CallDetail[];
  /** Interface-derived style of the whole connection (from its target interface). */
  readonly widgetStyle?: EdgeWidgetStyle;
  readonly widgetTag?: string;
}

/** Stable connection id for a call's deepest endpoints. */
export function connectionIdFor(sourceId: string, targetId: string): string {
  return `${sourceId}=>${targetId}`;
}

/** The connection ids a rendered piece (edge/port) carries — one per distinct
 *  (from,to) among its aggregated details. Derived, never stored, so the
 *  aggregation stays the single source of truth. */
export function connectionIdsFromDetails(details: readonly CallDetail[]): string[] {
  const ids: string[] = [];
  const seen = new Set<string>();
  for (const d of details) {
    const id = connectionIdFor(d.fromId, d.toId);
    if (!seen.has(id)) {
      seen.add(id);
      ids.push(id);
    }
  }
  return ids;
}

export interface BoardSpace {
  readonly id: string;
  readonly items: readonly BoardItem[];
  /**
   * Top-left corner of the frame in local coordinates. Usually (0, 0), but
   * goes negative when a hand-placed item has been dragged past the origin
   * so the frame can still wrap it.
   */
  readonly originX: number;
  readonly originY: number;
  /** Frame extents — width/height of the rectangle starting at (originX, originY). */
  readonly width: number;
  readonly height: number;
  /**
   * Edges between direct children of this space only.
   * (Cross-level edges are projected onto the lowest common ancestor.)
   */
  readonly edges?: readonly BoardEdge[];
  /**
   * `group by` frames (frame-groups): background rects the renderers draw
   * UNDER edges and items. One entry per (group × connected component) —
   * fragments of a group split across packed CCs share a `label`. Rects
   * are in this space's local coordinates (same frame as `items`),
   * computed post-solve as the member bbox + FRAME_PAD (+ FRAME_LABEL_H
   * top). Present only on the root space of a grouped view.
   */
  readonly groupFrames?: readonly PlacedGroupFrame[];
}

/** One placed `group by` frame fragment (see BoardSpace.groupFrames). */
export interface PlacedGroupFrame {
  /** Frame id; a CC-split fragment appends `::cc<n>`. */
  readonly id: string;
  readonly label: string;
  readonly x: number;
  readonly y: number;
  readonly w: number;
  readonly h: number;
  /** EVERY descendant member board id — the drag contract (dragging a
   *  parent label moves the whole subtree). */
  readonly memberIds: readonly string[];
  /** Nested `group by`: the parent FRAGMENT's id (same-CC binding). */
  readonly parentId?: string;
  /** Nesting depth (0 = top level) — the renderers' paint order + the
   *  depth-stepped fill. */
  readonly depth: number;
  /** This fragment's OWN members (the parent-pool for non-leaf frames). */
  readonly directMemberIds: readonly string[];
}

export interface BoardEdge {
  readonly id: string;
  readonly from: string;
  readonly to: string;
  /** Concrete endpoint paths aggregated into this edge, for drill-down. */
  readonly details: readonly CallDetail[];
  /**
   * Visual style derived from the `widget.*` config on the interfaces
   * this edge targets. Populated by the layout adapter from the
   * resolved model: each call's `toInterfaceId`/`toInterfacePath`
   * resolves to an Interface body whose `widget.*` fields are
   * extracted and aggregated. When the edge collapses calls to
   * interfaces with conflicting widget config, the conflicting prop
   * is left undefined and the renderer falls back to its default.
   *
   * Interaction states (selection, blast radius, process scrub) take
   * precedence over this style — it sets the *baseline* edge appearance.
   */
  /**
   * Figure/ground role, stamped by {@link applyFocus}. An edge is figure iff
   * BOTH its endpoints are — anything else draws a bright wire between two
   * receded boxes.
   */
  readonly focus?: FocusRole;
  /** True for aspect-web member→group edges. Drives a distinct (aspect-
   *  coloured) highlight treatment instead of the generic out/in/blast. */
  readonly isAspect?: boolean;
  /** True for a `replicated`-mode member→duplicate stub: a short data-
   *  association tether (not a routed call edge). The solver gives it a tiny
   *  ideal length so the copy hugs its consuming member; the renderer draws it
   *  as a short dashed nub. Implies {@link isAspect}. */
  readonly stub?: boolean;
  readonly widgetStyle?: EdgeWidgetStyle;
  /**
   * Custom-element tag for an HTML overlay rendered at the edge's
   * midpoint. The renderer mounts <foreignObject> there and registers
   * the element with structured props. Aggregated from the same
   * interfaces as widgetStyle: only set when every contributing
   * interface agrees on the tag.
   */
  readonly widgetTag?: string;
  /**
   * Inline-HTML template for the overlay (alternative to widgetTag).
   * Used as a tag-less fallback when the project hasn't shipped a
   * custom element. Placeholders use {{path}} substitution against
   * the interface's resolved fields, exactly like module templates.
   */
  readonly widgetTemplate?: string;
  /**
   * Other `widget.<name>` fields not consumed by style. Forwarded as
   * kebab-cased attributes on the overlay element. Empty when no
   * non-style props are configured.
   */
  readonly widgetProps?: ReadonlyMap<string, EdgeWidgetPropValue>;
  /**
   * Diff status against a base revision. When present and not `unchanged`,
   * the renderer applies a `diff-edge-<status>` className so the visible
   * stroke picks up the styling defined in `index.css`. `renamed` and
   * `unchanged` get no class — only structurally visible diffs (added,
   * removed, modified) are highlighted.
   */
  readonly diffStatus?: "added" | "removed" | "modified" | "renamed" | "unchanged";
  /**
   * Target-side row anchor: vertical fraction (0..1) along the `to`
   * item's wall where this edge should dock, instead of the wall
   * midpoint. Set by the layout adapter when the edge collapses to a
   * single target interface AND that interface's owning module is the
   * `to` item itself AND the item's widget exposes a `rowAnchorFrac`
   * hook (e.g. `arch-table`). Lets `Orders.user_id → Users.id` land on
   * the `id` column row. When present, the planner forces the to-side
   * to L/R (rows are horizontal bands) and uses this fraction in place
   * of slot allocation for that endpoint.
   */
  readonly toRowFrac?: number;
  /** True for a live-conformance UNDOCUMENTED edge: an observed-but-unmodeled
   *  call folded into the board build as a first-class violation edge (so it
   *  routes + auto-derives its stub port natively). A live overlay — painted as
   *  a violation (amber/dashed) and EXCLUDED from static export (render-svg) and
   *  from model-truth consumers. Never set on a modeled edge. */
  readonly violation?: boolean;
}

export type EdgeWidgetPropValue =
  | string
  | number
  | boolean
  | readonly EdgeWidgetPropValue[];

export interface EdgeWidgetStyle {
  /** CSS colour. When set, used as the edge stroke. */
  readonly color?: string;
  /** Render the line as dashed. */
  readonly dashed?: boolean;
  /** Stroke width in SVG units. */
  readonly width?: number;
  /** Line opacity (0..1). */
  readonly opacity?: number;
}

/** Explicit item placements that override auto-layout. */
export type PlacementOverrides = ReadonlyMap<
  string,
  { x: number; y: number; w: number; h: number }
>;
